import { useTranslation } from 'react-i18next'
import { Input } from 'components/ui/Input'
import { useFormik } from 'formik'
import { formFieldProps } from 'utils/form'
import { createValidationSchema, required } from 'utils/validations'
import { UIButton } from 'components/ui/Button'
import { KeyboardEvent } from 'react'
import { InputPassword } from 'components/ui/InputPassword'
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'components/ui/Modal'
import { useModal, useModalActions } from 'providers/ModalProvider'
import { useAuthFlow } from 'hooks/useAuthFlow'

type LoginModalProps = {
  onSuccess?: () => void
  onResetPassword?: () => void
  onRegister?: () => void
}

export const LoginModal = ({ onSuccess, onResetPassword, onRegister }: LoginModalProps) => {
  const { t } = useTranslation()
  const { close } = useModal()
  const { replaceWithResetPassword, replaceWithRegister } = useModalActions()
  const { handleLogin, isLoading } = useAuthFlow()

  const { values, errors, setFieldValue, setFieldError, handleSubmit } = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: createValidationSchema({
      email: required,
      password: required,
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values) => {
      try {
        await handleLogin(values, () => {
          onSuccess?.()
          close()
        })
      } catch {
        // Error already handled in hook
      }
    },
  })

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit()
    }
  }

  const onClickForgottenPassword = () => {
    if (onResetPassword) {
      return onResetPassword()
    }
    replaceWithResetPassword({ email: values.email })
  }

  const onClickRegister = () => {
    if (onRegister) {
      return onRegister()
    }
    replaceWithRegister()
  }

  const fieldProps = formFieldProps(errors, values, setFieldError, setFieldValue)

  return (
    <Modal onClose={close}>
      <ModalHeader title={t('login')} icon="right-to-bracket" />
      <ModalBody className={'tw-space-y-[12px]'}>
        <Input label={t('email')} {...fieldProps('email')} onKeyDown={onKeyDown} />
        <InputPassword label={t('password')} {...fieldProps('password')} onKeyDown={onKeyDown} />
        <div className={'tw-text-sm tw-underline tw-cursor-pointer'} onClick={onClickForgottenPassword}>
          {t('forgottenPassword')}
        </div>
        <div className={'tw-h-2'} />
        <div className={'tw-text-sm tw-cursor-pointer'} onClick={onClickRegister}>
          <span>{t('dontHaveAccount')}</span>
          <span className={'tw-underline'}>{t('register')}</span>
        </div>
      </ModalBody>
      <ModalFooter>
        <UIButton className={'tw-w-full'} onClick={() => handleSubmit()} loading={isLoading}>
          <span className={'tw-text-sm'}>{t('login')}</span>
        </UIButton>
      </ModalFooter>
    </Modal>
  )
}
